import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { Button, PhoneNumberField, TextField, Typography } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import {
  COLLECTED_DETAILS,
  COLLECTED_DETAILS_WITH_ADDRESS,
  CUSTOMER_ADDRESS_LABEL,
} from './booking-details-step.constants'
import { MAX_CUSTOMER_ADDRESS_LENGTH, MAX_EMAIL_LENGTH, MAX_FULL_NAME_LENGTH } from './booking-details-step.domain'
import type { BookingDetailsStepProps } from './booking-details-step.interface'
import { BookingQuestionsFields } from './booking-questions-fields'
import classes from './booking-details-step.module.scss'
import { useBookingDetailsStep } from './use-booking-details-step'

const EMAIL_FIELD_ID = 'booking-email'
const EMAIL_HINT_ID = `${EMAIL_FIELD_ID}-hint`
const EMAIL_ERROR_ID = `${EMAIL_FIELD_ID}-error`
const EMAIL_HINT = 'We will email your appointment details here. We do not use this address for marketing.'
const ADDRESS_FIELD_ID = 'booking-customer-address'
const ADDRESS_ERROR_ID = `${ADDRESS_FIELD_ID}-error`

export function BookingDetailsStep(props: BookingDetailsStepProps) {
  const v = useBookingDetailsStep(props)

  // Screen readers get the hint AND the error when both are on screen, in the
  // order they are rendered, so the reason we ask is never traded for the fix.
  const emailDescribedBy = [EMAIL_HINT_ID, v.emailError ? EMAIL_ERROR_ID : null].filter(Boolean).join(' ')

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.formCard}>
          <div className={classes.cardHeader}>
            <Typography variant="h2" className={classes.cardTitle}>
              Complete Your Booking
            </Typography>
          </div>
          <Typography variant="body" className={classes.cardDescription}>
            Please provide your details to confirm your appointment
          </Typography>

          {v.selectedDate && v.selectedTime && (
            <div className={classes.summaryBox}>
              <Typography variant="body" className={classes.summaryTitle}>
                Appointment Summary
              </Typography>
              <Typography variant="body" className={classes.summaryItem}>
                {v.appointmentTypeLabel}
              </Typography>
              <Typography variant="body" className={classes.summaryItem}>
                {format(v.selectedDate, 'EEEE, MMMM d')}
              </Typography>
              <Typography variant="body" className={classes.summaryItem}>
                {v.selectedTime} - {v.endTimeDisplayForSummary} {v.timezone && `(${v.timezone})`}
              </Typography>
            </div>
          )}

          {v.errorMessage && (
            <div className={classes.errorMessage} style={{ color: 'red', marginBottom: '1rem', padding: '0.75rem' }}>
              <Typography variant="body" style={{ color: 'red' }}>
                {v.errorMessage}
              </Typography>
            </div>
          )}

          <div className={classes.formFields}>
            <TextField
              label="Full Name"
              value={v.fullName}
              onChange={(e) => v.handleFullNameChange(e.target.value)}
              onBlur={v.handleFullNameBlur}
              error={v.fullNameError ?? undefined}
              placeholder="Enter your full name"
              maxLength={MAX_FULL_NAME_LENGTH}
            />

            <PhoneNumberField
              value={v.phoneNumber}
              onChange={(value) => v.handlePhoneNumberChange(value ?? '')}
              error={v.phoneNumberError ?? undefined}
            />

            <TextField
              id={EMAIL_FIELD_ID}
              type="email"
              label="Email"
              value={v.email}
              onChange={(e) => v.handleEmailChange(e.target.value)}
              onBlur={v.handleEmailBlur}
              error={v.emailError ?? undefined}
              errorId={EMAIL_ERROR_ID}
              hint={EMAIL_HINT}
              hintId={EMAIL_HINT_ID}
              placeholder="you@example.com"
              maxLength={MAX_EMAIL_LENGTH}
              required
              aria-required
              aria-invalid={v.emailError ? true : undefined}
              aria-describedby={emailDescribedBy}
            />

            {v.addressField.isRequired && (
              <TextField
                id={ADDRESS_FIELD_ID}
                label={CUSTOMER_ADDRESS_LABEL}
                value={v.addressField.value}
                onChange={(e) => v.addressField.handleChange(e.target.value)}
                onBlur={v.addressField.handleBlur}
                error={v.addressField.error ?? undefined}
                errorId={ADDRESS_ERROR_ID}
                maxLength={MAX_CUSTOMER_ADDRESS_LENGTH}
                required
                aria-required
                aria-invalid={v.addressField.error ? true : undefined}
                aria-describedby={v.addressField.error ? ADDRESS_ERROR_ID : undefined}
              />
            )}

            <BookingQuestionsFields
              questions={v.questionsField.questions}
              answers={v.questionsField.answers}
              errors={v.questionsField.errors}
              onChange={v.questionsField.handleChange}
              onBlur={v.questionsField.handleBlur}
            />
          </div>

          <Typography variant="body" className={classes.privacyNotice}>
            We collect your {v.addressField.isRequired ? COLLECTED_DETAILS_WITH_ADDRESS : COLLECTED_DETAILS} to book
            this appointment and to email you the confirmation and its details. See our{' '}
            <Link to={routeNames.privacy.path} target="_blank" rel="noreferrer" className={classes.privacyLink}>
              Privacy Policy
            </Link>
            .
          </Typography>

          <div className={classes.actionsRow}>
            <Button variant="secondary" onClick={v.onBack} className={classes.backButton} disabled={v.isConfirmLoading}>
              Back
            </Button>
            <Button
              onClick={v.handleConfirm}
              disabled={!v.canConfirm}
              className={classes.confirmButton}
              isLoading={v.isConfirmLoading}
            >
              Confirm Booking
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
